import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormikProvider } from 'formik';
import { LoadingButton } from '@mui/lab';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useNationalTaxServiceCredentials from '../../hooks/useNationalTaxServiceCredentials';
import EmailField from '../fields/EmailField';
import PasswordField from '../fields/PasswordField';
import NationalIdentifierField from '../fields/NationalIdentifierField';
import useAcceptTermsCheckboxes from '../../hooks/useAcceptTermsCheckboxes';
import FormCheckboxStack from './FormCheckboxStack';

const NationalTaxServiceCredentialsForm = ({
  autocomplete,
  source,
  mainStackSpacing,
  onCompleted,
  successMessage,
  closeDialogOnSuccess,
  showCheckboxes,
  showEmail,
  buttonText,
  nationalIdentifierInputProps,
  passwordInputProps,
  buttonProps,
  signTyc,
}) => {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptDataUsage, setAcceptDataUsage] = useState(false);

  const checkboxOptions = useAcceptTermsCheckboxes({
    acceptTerms,
    setAcceptTerms,
    acceptDataUsage,
    setAcceptDataUsage,
  });

  const { formik, initialNationalIdentifier, t } = useNationalTaxServiceCredentials({
    autocomplete,
    source,
    onCompleted,
    successMessage,
    closeDialogOnSuccess,
    showEmail,
    signTyc,
  });

  const { isValid, isSubmitting } = formik;
  const canSubmit = isValid && (
    (acceptTerms && acceptDataUsage) || !showCheckboxes) && !isSubmitting;

  return (
    <FormikProvider value={formik}>
      <form
        onSubmit={formik.handleSubmit}
      >
        <Stack
          spacing={mainStackSpacing}
          direction="column"
        >
          <Stack spacing={3} direction="column">
            <NationalIdentifierField
              disabled={autocomplete && Boolean(initialNationalIdentifier)}
              {...nationalIdentifierInputProps}
            />
            <PasswordField
              variant="outlined"
              label={t('Credential Password')}
              {...passwordInputProps}
            />
            {showEmail && <EmailField variant="outlined" label="Correo Electrónico" />}
          </Stack>
          {showCheckboxes && (
            <FormCheckboxStack
              spacing={0}
              options={checkboxOptions}
            />
          )}
          <Box display="flex" justifyContent="center">
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              loading={isSubmitting}
              disabled={!canSubmit}
              sx={{ maxWidth: 300 }}
              {...buttonProps}
            >
              <Typography
                variant="small_3"
                fontWeight={700}
              >
                {buttonText}
              </Typography>
            </LoadingButton>
          </Box>
        </Stack>
      </form>
    </FormikProvider>
  );
};

NationalTaxServiceCredentialsForm.propTypes = {
  source: PropTypes.string,
  autocomplete: PropTypes.bool,
  mainStackSpacing: PropTypes.number,
  onCompleted: PropTypes.func,
  successMessage: PropTypes.string,
  closeDialogOnSuccess: PropTypes.bool,
  showCheckboxes: PropTypes.bool,
  showEmail: PropTypes.bool,
  buttonText: PropTypes.string,
  nationalIdentifierInputProps: PropTypes.shape({}),
  passwordInputProps: PropTypes.shape({}),
  buttonProps: PropTypes.shape({}),
  signTyc: PropTypes.bool,
};

NationalTaxServiceCredentialsForm.defaultProps = {
  source: null,
  autocomplete: false,
  mainStackSpacing: 3,
  onCompleted: () => null,
  successMessage: 'Credenciales ingresadas con éxito',
  closeDialogOnSuccess: true,
  showCheckboxes: false,
  showEmail: false,
  buttonText: 'Registrar empresa',
  nationalIdentifierInputProps: {},
  passwordInputProps: {},
  buttonProps: {},
  signTyc: true,
};

export default NationalTaxServiceCredentialsForm;
